import React, { useRef } from 'react';
import { ArrowUpRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { products } from '../data/products';
import type { Product } from '../types/product';

/**
 * The landing page's product ecosystem. Three equal-weight cards, because the
 * three products genuinely are siblings — the hierarchy is carried by each
 * card's own signature colour, not by size.
 *
 * The set-piece: the cards sit offset from one another and settle into
 * alignment as the section is scrolled into view.
 */
export function Products() {
  const ref = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'center center']
  });
  return <section ref={ref} id="products" className="bg-paper px-5 py-24 md:px-10 md:py-32" aria-labelledby="products-heading" data-id="element-652" data-mp="16ilsbjzots5b">
      <div className="mx-auto max-w-shell" data-id="element-653" data-mp="12ee37lpbia6k">
        <div className="grid gap-8 border-b border-ink/15 pb-12 md:grid-cols-[1fr_0.8fr] md:items-end" data-id="element-654" data-mp="18vqcbnv4h53g">
          <div data-id="element-655" data-mp="1dvmintral8m6">
            <p className="text-xs font-extrabold tracking-[0.16em] text-ink/55" data-id="element-656" data-mp="1dw5rmkyysmo2">
              THREE WAYS INTO A PRODUCT MOMENT
            </p>
            <h2 id="products-heading" className="mt-5 font-display text-5xl font-bold leading-[0.88] md:text-7xl" data-id="element-657" data-mp="zxxzcqcvi9j8">
              One platform.
              <br data-id="element-658" data-mp="1roubtpv0uc5z" />
              Three products.
            </h2>
          </div>
          <p className="max-w-xl text-lg leading-relaxed text-ink/70" data-id="element-659" data-mp="1wlcemk6sk0fo">
            Each product owns a different moment in the loop — discovery,
            trial, and targeting — and each one returns a signal the next
            campaign can use.
          </p>
        </div>

        <div className="mt-12 grid gap-4 lg:grid-cols-3" data-id="element-660" data-mp="2akkfdhilrglz">
          {products.map((product, index) => <ProductCard key={product.slug} product={product} index={index} progress={scrollYProgress} data-id="element-661" data-mp="2fd578dhy7jmv" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}
function ProductCard({
  product,
  index,
  progress
}: {
  product: Product;
  index: number;
  progress: ReturnType<typeof useScroll>['scrollYProgress'];
}) {
  const reduceMotion = useReducedMotion();
  const offset = [34, 68, 102][index] ?? 0;
  const y = useTransform(progress, [0, 1], [offset, 0]);
  return <motion.article style={reduceMotion ? undefined : {
    y
  }} data-id="element-662" data-mp="26baxnxlbtg04">
      <Link to={product.path} className={`flex h-full min-h-[420px] flex-col p-7 transition-transform hover:-translate-y-1 md:p-9 ${product.theme.surface} ${product.theme.onSurface}`} data-id="element-663" data-mp="1xgd0uff25j3i">
        <div className="flex items-baseline justify-between" data-id="element-664" data-mp="1nhx704onbvqb">
          <p className={`text-xs font-extrabold tracking-[0.14em] ${product.theme.eyebrow}`} data-id="element-665" data-mp="1ijvn5hjebsds">
            PRODUCT {product.number}
          </p>
          <span className={`h-2.5 w-2.5 rounded-full ${product.theme.railMark}`} aria-hidden="true" data-id="element-666" data-mp="18fp3ithk1eo4" />
        </div>

        <h3 className="mt-12 font-display text-4xl font-bold leading-[0.9]" data-id="element-667" data-mp="1idldvj2o45l6">
          {product.name}
        </h3>
        <p className={`mt-6 text-base leading-relaxed ${product.theme.onSurfaceMuted}`} data-id="element-668" data-mp="1nc6c5cyd0f6z">
          {product.heroCopy}
        </p>

        <div className={`mt-8 border-t pt-6 ${product.theme.hairline}`} data-id="element-669" data-mp="pnmg8zaer04w">
          <p className="text-xs font-extrabold tracking-[0.1em]" data-id="element-670" data-mp="up26d5rfzwu5">
            {product.outcome.toUpperCase()}
          </p>
        </div>

        <span className="mt-auto flex items-center gap-2 pt-8 text-sm font-extrabold" data-id="element-671" data-mp="14ed9mbrne7a4">
          EXPLORE {product.shortName.toUpperCase()}
          <ArrowUpRightIcon size={16} aria-hidden="true" data-id="element-672" data-mp="6xwl2b2qgccg" />
        </span>
      </Link>
    </motion.article>;
}